"use client"

import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";

import { RetrievedBlogCategory, RetrievedProductCategory } from "@/lib/sanity/responseTypes";
import { ArticleInfo, ProductInfo } from "@/types";

import PageLayout from "@/components/PageLayout";
import Title from "@/components/Title";
import NavigationFilters from "@/components/NavigationFilters";
import ProductsGallery from "@/components/ecommerce/products-gallery";
import PostGallery from "@/components/cms/post-gallery";
import { AllProductsReturnType } from "@/lib/server/shop/products/all-products";

interface CategoryPageViewProps {
  locale: string;
  slug?: string;
  catalogue?: AllProductsReturnType;
}

export default function CategoryPageView(props: CategoryPageViewProps) {
  /* ——— Params & translations ——— */
  const { locale: localeParam, slug: slugParam, catalogue } = props;
  const t = useTranslations("Category");

  const locale: string = localeParam as string;
  const slug: string | undefined = slugParam as string | undefined;

  /* ——— Local state ——— */
  const [productsCategories, setProductsCategories] = useState<RetrievedProductCategory[]>(catalogue?.productsCategories || []);
  const [articlesCategories, setArticlesCategories] = useState<RetrievedBlogCategory[]>(catalogue?.articlesCategories || []);
  const [products, setProducts] = useState<ProductInfo[]>(catalogue?.productsWithPrice || []);
  const [articles, setArticles] = useState<ArticleInfo[]>(catalogue?.articles || []);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterVisible, setFilterVisible] = useState<boolean>(false);

  const [productsSliceEnd, setProductsSliceEnd] = useState<number>(10);
  const [productsSlice, setProductsSlice] = useState<ProductInfo[]>([]);

  const [navigationFiltersItems, setNavigationFiltersItems] = useState<{
    label: string,
    doNotTranslate?: boolean,
    active: boolean,
    path: string,
  }[]>([]);
  const [currentCategory, setCurrentCategory] = useState<{
    label: string,
    doNotTranslate?: boolean,
    active: boolean,
    path: string,
  } | null>(null);

  useEffect(() => {
    setProductsSlice(products.slice(0, productsSliceEnd));
  }, [products, productsSliceEnd]);

  /* ——— Fetch via our API route ——— */
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      try {
        const relevantAPIPath = slug && slug.length > 0 ? `/api/category/${slug}?locale=${locale}` : `/api/category?locale=${locale}`;
        const res = await fetch(relevantAPIPath, {
          next: { revalidate: 60 }
        });
        if (!res.ok) throw new Error("API error");

        const { products, productsWithPrice, articles, productsCategories, articlesCategories } = await res.json();
        if (!cancelled) {
          setProducts(productsWithPrice);
          setProductsSlice(productsWithPrice.slice(0, productsSliceEnd));
          setArticles(articles);
          setProductsCategories(productsCategories);
          setArticlesCategories(articlesCategories);
          setError(null);

          document.title = `Shop - Invento`;
        }
      } catch (err) {
        if (!cancelled) setError("Failed to load data");
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [slug, locale]);

  useEffect(() => {
    let filterItems = [
      { label: "categories_all", active: !slug, path: "/kategorie" },
    ];

    let filterItemsCategories = productsCategories.map((p, i) => {
      let relevantName = p.title[locale];
      let categoryObject = {
        label: relevantName,
        doNotTranslate: true,
        active: p.slug === slug,
        path: `/kategorie/${p.slug}`,
        slug: p.slug
      };

      return categoryObject;
    })

    let currentCategoryItem: {
      label: string,
      doNotTranslate?: boolean,
      active: boolean,
      path: string,
      slug: string,
    } | undefined = filterItemsCategories.find(c => c.slug === slug);

    filterItems = filterItems.concat(filterItemsCategories);
    setNavigationFiltersItems(filterItems);
    setCurrentCategory(currentCategoryItem!);

    if (currentCategoryItem) {
      document.title = `${currentCategoryItem.label} - Invento`;
    } else {
      document.title = `Shop - Invento`;
    }
  }, [slug, locale, productsCategories]);

  function loadMore() {
    setProductsSliceEnd(productsSliceEnd + 10);
  }

  function hasMore() {
    return productsSliceEnd < products.length;
  }

  /* ——— Early states ——— */
  if (loading) {
    return (
      <PageLayout padded={true} gapPx={30}>
        <Title breadcrumbs={[{ title: 'Shop', path: '/kategorie' }]} title={currentCategory && currentCategory.label || t('categories_all')}></Title>
        <NavigationFilters items={navigationFiltersItems} translationCollection="Category" />
      </PageLayout >
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="p-10 text-center">
          <h2>{error}</h2>
          <p>Please try again later.</p>
        </div>
      </PageLayout>
    );
  }

  /* ——— Main render ——— */
  return (
    <PageLayout padded={true} gapPx={20}>
      <Title breadcrumbs={[{ title: 'Shop', path: '/kategorie' }]} title={currentCategory && currentCategory.label || t('categories_all')}></Title>
      <NavigationFilters items={navigationFiltersItems} translationCollection="Category" />

      {/* ---- Products ---- */}
      <ProductsGallery itemWidth={200} imageHeight={260} itemsPerRow={5} products={productsSlice} hasMore={hasMore()} loadMore={loadMore} />
      {/* ---- Blog posts ---- */}
      {articles && articles.length > 0 && (
        <>
          <Title noBreadcrumbs={true} title={t('blog-title')}></Title>
          <PostGallery articles={articles.slice(0, 3)} itemsPerRow={3} itemWidth={400} imageHeight={275} readMoreLabel={t('blog-show-more')} showMagazineLink={true} />
        </>
      )}
    </PageLayout>
  );
}
